import React, { useState, useEffect } from 'react';
import { Layout, Menu, Image, Flex, Typography, Avatar } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import logo from '../../assets/logo/logo.svg';
import profileImage from '../../assets/images/avatar.jpg';
import '../../styles/layout/sidebar.scss';
import { menuName } from '../../utils/constraints';
import WorkHistory from '../../pages/settings/WorkHistory';
import { getCompany } from '../../pages/settings/redux/slice';

const { Sider } = Layout;
const { Title } = Typography;

const drawerWidth = 300;

const SidebarComponent = ({ collapsed, setCollapsed, filteredRoutes }) => {
    const listing = useSelector(state => state.listing);
    const profile = useSelector(state => state.profile);
    const auth = useSelector(state => state.auth);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [openKeys, setOpenKeys] = useState([]);

    useEffect(() => {
        if (Object.keys(auth?.decodeToken ?? {}).length > 0 && Object.keys(profile?.company ?? {}).length === 0) {
            dispatch(getCompany(auth.decodeToken._id));
        }
    }, [auth.decodeToken]);
    const [selectedKey, setSelectedKey] = useState('');

    const location = useLocation();

    const findOpenKeys = (routes, pathname) => {
        for (const route of routes) {
            if (route.children) {
                const childRoute = route.children.find(child => child.label?.props?.to === pathname);
                if (childRoute) {
                    return [route.key];
                } else {
                    const parentKey = findOpenKeys(route.children, pathname);
                    if (parentKey) return parentKey;
                }
            }
        }
        return null;
    };

    useEffect(() => {
        if (filteredRoutes?.length > 0) {
            const currentRoute = filteredRoutes.find(route => route.label?.props?.to === location.pathname);

            if (currentRoute) {
                setSelectedKey(currentRoute.key);
            } else {
                const openKey = findOpenKeys(filteredRoutes, location.pathname);
                if (openKey) {
                    setOpenKeys(openKey);
                    const childRoute = filteredRoutes.flatMap(route => route.children || [])
                        .find(child => child.label?.props?.to === location.pathname);
                    if (childRoute) {
                        setSelectedKey(childRoute.key);
                    }
                }
            }
        }
    }, [location, filteredRoutes]);

    const onOpenChange = (keys) => {
        const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
        setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    };

    return (
        <Sider
            className='rounded-2'
            breakpoint='lg'
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={drawerWidth}
            onBreakpoint={(broken) => setCollapsed(broken)}
        >
            <Flex align='center' justify='center' gap={16} className='padding-1'>
                <Image src={logo} preview={false} />
                {!collapsed && <Title type='danger' level={3}>Bau Scape</Title>}
            </Flex>
            {Object.keys(profile?.company ?? {}).length > 0 && (
                <Flex vertical align='center' justify='center' gap={4} className='paddingX-1 padding-bottom-1'>
                    <Avatar
                        size={collapsed ? 36 : 56}
                        src={profile.company.profilePicture
                            ? `${process.env.REACT_APP_API_URI}/company/${profile.company.profilePicture}`
                            : profileImage}
                    />
                    {!collapsed && (
                        <Title level={5} className='margin-0' style={{ textAlign: 'center', wordBreak: 'break-word' }}>
                            {profile.company.name}
                        </Title>
                    )}
                </Flex>
            )}
            <Menu
                mode='inline'
                theme='light'
                className='rounded-2 padding-top-2 sidebar-menu'
                selectedKeys={[selectedKey]}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                // items={filteredRoutes?.map(i => {
                //     const timeTrackingMenu = filteredRoutes.find(route => route.menu === menuName.timeTracking);
                //     const vacationManagement = filteredRoutes.find(route => route.menu === menuName.vacationManagement);

                //     if (Object.keys(timeTrackingMenu ?? {}).length === 0) {
                //         return {
                //             ...i,
                //             label: listing?.language === 'en' ? i.label : i.germanLabel,
                //             ...(i?.children) && {
                //                 children: i?.children?.map(x => {
                //                     return {
                //                         ...x,
                //                         label: listing?.language === 'en' ? x.label : x.germanLabel
                //                     }
                //                 }).filter((item) => item.menu !== menuName.workHistory)
                //             }
                //         }
                //     }

                //     if (Object.keys(vacationManagement ?? {}).length === 0) {
                //         return {
                //             ...i,
                //             label: listing?.language === 'en' ? i.label : i.germanLabel,
                //             ...(i?.children) && {
                //                 children: i?.children?.map(x => {
                //                     return {
                //                         ...x,
                //                         label: listing?.language === 'en' ? x.label : x.germanLabel
                //                     }
                //                 }).filter((item) => item.menu !== menuName.addVacation)
                //             }
                //         }
                //     }
                //     return {
                //         ...i,
                //         label: listing?.language === 'en' ? i.label : i.germanLabel,
                //         ...(i?.children) && {
                //             children: i?.children?.map(x => {
                //                 return {
                //                     ...x,
                //                     label: listing?.language === 'en' ? x.label : x.germanLabel
                //                 }
                //             })
                //         }
                //     }
                // })}
                items={filteredRoutes?.map(i => {
                    const timeTrackingMenu = filteredRoutes.find(route => route.menu === menuName.timeTracking);
                    const vacationManagement = filteredRoutes.find(route => route.menu === menuName.vacationManagement);

                    const getLabel = (item) => {
                        if (!item.label) return item.label;
                        if (item.translationKey && item.linkTo) {
                            return <Link to={item.linkTo}>{t(item.translationKey)}</Link>;
                        }
                        if (item.translationKey) {
                            return t(item.translationKey);
                        }
                        return listing?.language === 'en' ? item.label : item.germanLabel;
                    };

                    // Only pass props that Ant Design Menu understands
                    const toMenuItem = (item) => ({
                        key: item.key,
                        icon: item.icon,
                        disabled: item.disabled,
                        label: getLabel(item),
                        ...(item.menu !== undefined && { 'data-menu': item.menu }),
                    });

                    const noTimeTracking = Object.keys(timeTrackingMenu ?? {}).length === 0;
                    const noVacation = Object.keys(vacationManagement ?? {}).length === 0;

                    const hiddenMenus = [
                        ...(noTimeTracking ? [menuName.workHistory] : []),
                        ...(noVacation ? [menuName.addVacation] : []),
                    ];

                    const menuItem = toMenuItem(i);
                    if (i?.children?.length > 0) {
                        menuItem.children = i.children
                            .filter(c => !hiddenMenus.includes(c.menu))
                            .map(toMenuItem);
                    }
                    return menuItem;
                })}

            />
        </Sider>
    );
};

export default SidebarComponent;
